import { apiGet } from './api';

function appendRequesterUserId(query: URLSearchParams): URLSearchParams {
	if (typeof window === 'undefined') {
		return query;
	}
	try {
		const raw = window.sessionStorage.getItem('gardenhub:auth');
		if (!raw) return query;
		const user = JSON.parse(raw) as { id?: string | number } | null;
		if (user?.id != null && !query.has('userId')) {
			query.set('userId', String(user.id));
		}
	} catch {
		// Ignore malformed session payloads and let the backend reject unauthenticated admin reads.
	}
	return query;
}

export type ActivityOverviewResponse = {
	window_days: number | null;
	window_start_date: string | null;
	window_end_date: string;
	is_custom_range: boolean;
	is_all_time?: boolean;
	page_path: string;
	metrics: {
		seeker_home_views: number;
		page_views: number;
		unique_visitors: number;
		logged_in_visitors: number;
		anonymous_visitors: number;
		avg_engaged_seconds: number;
		cta_clicks: number;
		login_clicks: number;
		login_successes: number;
		questionnaire_starts: number;
		questionnaire_submits: number;
		plan_generation_starts: number;
		plan_generation_successes: number;
		plan_generation_errors: number;
		total_events: number;
		total_sessions: number;
		total_anonymous_ids: number;
		total_known_users: number;
		new_users: number;
		logged_in_events: number;
		anonymous_events: number;
		avg_session_seconds: number;
	};
	daily: Array<{
		day: string;
		views: number;
		unique_visitors: number;
		logged_in_visitors: number;
		anonymous_visitors: number;
		sessions: number;
		events: number;
		known_users: number;
		anonymous_ids: number;
		questionnaire_starts: number;
		questionnaire_submits: number;
		plan_generation_successes: number;
		new_users: number;
	}>;
	top_ctas: Array<{
		cta_name: string | null;
		clicks: number;
	}>;
	event_breakdown: Array<{
		event_name: string;
		events: number;
		sessions: number;
		avg_duration_seconds: number | null;
	}>;
	page_breakdown: Array<{
		page_key: string;
		views: number;
		events: number;
		sessions: number;
		unique_visitors: number;
		avg_heartbeat_seconds: number | null;
	}>;
	device_breakdown: Array<{
		device_type: string;
		events: number;
		sessions: number;
	}>;
	browser_breakdown: Array<{
		browser: string;
		events: number;
		sessions: number;
	}>;
	country_breakdown?: Array<{
		country_code: string;
		events: number;
		sessions: number;
		unique_visitors: number;
	}>;
	funnel: Array<{
		key: string;
		label: string;
		count: number;
	}>;
	questionnaire_steps: Array<{
		step_number: number;
		views: number;
		next_clicks: number;
		validation_errors: number;
		avg_exit_seconds: number | null;
	}>;
	questionnaire_exits: Array<{
		step_number: number;
		exits: number;
	}>;
	friction_signals: {
		questionnaire_validation_errors: number;
		questionnaire_upload_errors: number;
		questionnaire_ai_analysis_errors: number;
		plan_generation_errors: number;
		plan_flow_missing_questionnaire: number;
		plan_flow_handoff_storage_failed: number;
		login_failures: number;
		signup_failures: number;
	};
	recent_events: Array<{
		created_at: string;
		event_name: string;
		page_path: string | null;
		session_id: string;
		anonymous_id: string;
		user_id: number | null;
		is_logged_in: number;
		duration_seconds: number | null;
		device_type: string | null;
		browser: string | null;
		country_code?: string | null;
	}>;
};

export async function getAdminActivityOverview(params?: {
	/** Pass `0` for all-time totals. */
	days?: number;
	startDate?: string;
	endDate?: string;
	pagePath?: string;
}): Promise<ActivityOverviewResponse> {
	const query = new URLSearchParams();
	if (params?.days != null) query.set('days', String(params.days));
	if (params?.startDate) query.set('start_date', params.startDate);
	if (params?.endDate) query.set('end_date', params.endDate);
	query.set('page_path', params?.pagePath ?? '/');
	appendRequesterUserId(query);
	return apiGet<ActivityOverviewResponse>(`/admin/activity/overview?${query.toString()}`);
}

export type UserActivityMatch = {
	id: number;
	name: string;
	email: string;
	role: string;
	company_name: string | null;
};

export type UserActivityMatchResponse = {
	matches: UserActivityMatch[];
};

export type ActivityDrilldownUser = {
	id: number;
	name: string;
	email: string;
	role: string;
	company_name: string | null;
	created_at: string;
};

export type ActivityDrilldownPlan = {
	id: number;
	title: string;
	status: string;
	moderation_status: string | null;
	created_at: string;
	owner_user_id: number | null;
	owner_name: string | null;
	owner_email: string | null;
};

export type ActivityDrilldownVisitor = {
	identity_key: string;
	user_id: number | null;
	name: string | null;
	email: string | null;
	role: string | null;
	company_name: string | null;
	is_logged_in: boolean;
	view_count: number;
	session_count: number;
	first_seen_at: string;
	last_seen_at: string;
};

export type ActivityDrilldownResponse =
	| {
			kind: 'user_registrations';
			month: string;
			total: number;
			users: ActivityDrilldownUser[];
	  }
	| {
			kind: 'plans_created';
			month: string;
			total: number;
			plans: ActivityDrilldownPlan[];
	  }
	| {
			kind: 'page_visitors';
			day: string;
			page_path: string;
			total: number;
			visitors: ActivityDrilldownVisitor[];
	  };

export type UserActivityResponse = {
	window_days: number | null;
	user: {
		id: number;
		name: string;
		email: string;
		role: string;
		company_name: string | null;
		created_at: string;
		updated_at: string;
	};
	activity: {
		first_seen_at: string | null;
		last_seen_at: string | null;
		active_days: number;
		total_sessions: number;
		total_events: number;
		engaged_seconds: number;
		login_successes: number;
		questionnaire_starts: number;
		questionnaire_submits: number;
		plan_generation_starts: number;
		plan_generation_successes: number;
		plan_generation_errors: number;
		questionnaire_validation_errors: number;
		questionnaire_upload_errors: number;
	};
	plans: {
		total: number;
		draft: number;
		published: number;
		closed: number;
		archived: number;
		hidden: number;
		deleted: number;
	};
	projects: {
		total: number;
		planning: number;
		quoted: number;
		in_progress: number;
		completed: number;
		cancelled: number;
	};
	offers: {
		received_on_plans: number;
		sent_as_gardener: number;
		received_by_status?: {
			accepted: number;
			pending: number;
			rejected: number;
			other: number;
		};
		sent_by_status?: {
			accepted: number;
			pending: number;
			rejected: number;
			other: number;
		};
	};
	top_events: Array<{ event_name: string; events: number }>;
	devices: Array<{ device_type: string; events: number }>;
	browsers: Array<{ browser: string; events: number }>;
	daily_activity: Array<{
		day: string;
		events: number;
		sessions: number;
		engaged_seconds: number;
	}>;
	recent_events: Array<{
		created_at: string;
		event_name: string;
		page_path: string | null;
		duration_seconds: number | null;
		device_type: string | null;
		browser: string | null;
	}>;
	plans_list: Array<{
		id: number;
		title: string;
		status: string;
		moderation_status: string | null;
		generation_duration_seconds: number | null;
		created_at: string;
		updated_at: string;
		published_at: string | null;
	}>;
};

export async function getAdminUserActivity(params: {
	userId?: number;
	q?: string;
	days?: number;
}): Promise<UserActivityResponse | UserActivityMatchResponse> {
	const query = new URLSearchParams();
	if (params.userId != null) {
		query.set('user_id', String(params.userId));
	} else if (params.q) {
		query.set('q', params.q);
	}
	if (params.days != null) {
		query.set('days', String(params.days));
	}
	appendRequesterUserId(query);
	return apiGet<UserActivityResponse | UserActivityMatchResponse>(
		`/admin/activity/user?${query.toString()}`
	);
}

export function isUserActivityMatchResponse(
	data: UserActivityResponse | UserActivityMatchResponse
): data is UserActivityMatchResponse {
	return 'matches' in data && Array.isArray(data.matches);
}

export async function getAdminActivityDrilldown(params: {
	kind: 'user_registrations' | 'plans_created';
	month: string;
} | {
	kind: 'page_visitors';
	day: string;
	pagePath?: string;
}): Promise<ActivityDrilldownResponse> {
	const query = new URLSearchParams();
	query.set('kind', params.kind);
	if ('month' in params) {
		query.set('month', params.month);
	}
	if ('day' in params) {
		query.set('day', params.day);
		query.set('page_path', params.pagePath ?? '/');
	}
	appendRequesterUserId(query);
	return apiGet<ActivityDrilldownResponse>(`/admin/activity/drilldown?${query.toString()}`);
}

export type PlanCreationErrorCategory =
	| 'validation'
	| 'upload'
	| 'analysis'
	| 'generation'
	| 'plan_flow';

export type PlanCreationErrorItem = {
	id: number;
	event_name: string;
	summary: string;
	page_path: string | null;
	session_id: string;
	anonymous_id: string;
	user_id: number | null;
	user_name: string | null;
	user_email: string | null;
	user_role: string | null;
	is_logged_in: boolean;
	device_type: string | null;
	browser: string | null;
	locale: string | null;
	properties: Record<string, unknown> | null;
	created_at: string;
};

export type PlanCreationErrorsResponse = {
	window_days: number | null;
	window_start_date: string | null;
	window_end_date: string;
	is_custom_range: boolean;
	is_all_time?: boolean;
	total: number;
	limit: number;
	offset: number;
	summary_by_event: Array<{ event_name: string; count: number }>;
	items: PlanCreationErrorItem[];
};

export async function getAdminPlanCreationErrors(params?: {
	days?: number;
	startDate?: string;
	endDate?: string;
	category?: PlanCreationErrorCategory;
	eventName?: string;
	q?: string;
	limit?: number;
	offset?: number;
}): Promise<PlanCreationErrorsResponse> {
	const query = new URLSearchParams();
	if (params?.days != null) query.set('days', String(params.days));
	if (params?.startDate) query.set('start_date', params.startDate);
	if (params?.endDate) query.set('end_date', params.endDate);
	if (params?.category) query.set('category', params.category);
	if (params?.eventName) query.set('event_name', params.eventName);
	if (params?.q) query.set('q', params.q);
	if (params?.limit != null) query.set('limit', String(params.limit));
	if (params?.offset != null) query.set('offset', String(params.offset));
	appendRequesterUserId(query);
	return apiGet<PlanCreationErrorsResponse>(`/admin/activity/errors?${query.toString()}`);
}
